import { HTMLAttributes, ReactEventHandler, useEffect } from 'react';
import { FiltersIcon } from '../../../../lib/icons/FiltersIcon';
import styles from './FilterPanel.module.scss';
import FilterSearchBox from './FilterSearchBox/FilterSearchBox';
import { classes } from '../../../../lib/utils/cssUtils';
import { Label } from '../../../common/Label/Label';
import { FILTER_LABELS, COMMON_LABELS } from '../../../../lib/constants/labelKeys';
import { FILTERS_DEFAULT_SORTING_VALUES } from '../../../../lib/constants/courses';
import WithDevice from '../../../common/WithDevice/WithDevice';
import { RightArrow } from '../../../../lib/icons/RightArrow';
import { useDevice } from '../../../../lib/hooks/device';
import Button from '../../../common/Button/Button';
import { CloseIcon } from '../../../../lib/icons/CloseIcon';
import { updateByProp } from '../../../../lib/utils/updateByProp';
import { useAttributeLabel } from '../../../../lib/hooks/labels';
import { ATTRIBUTES } from '../../../../lib/constants/attributes';
import { Facet, FacetType, FilterSortBy } from '../../../../lib/utils/search/searchModel';
import FacetView from './Facet/Facet';
import SortingFacet from './SortingFacet/SortingFacet';
import { useFiltersView } from '../../../../lib/context/FiltersViewContext';
import useMobileExpanded from '../../../../lib/hooks/useMobileExpanded';
import useDebouncedCallback from '../../../../lib/hooks/useDebouncedCallback';
import { DEFAULT_DEBOUNCE_TIME } from '../../../../lib/constants/globals';
import { isNil } from '../../../../lib/utils/isNil';
import SkeletonFacets from './Facet/SkeletonFacets';
import useFeature from '../../../../lib/hooks/useFeature';
import { FeatureToggleCodename } from '../../../../lib/types/enums';
import { Api } from '../../../../lib/types/apiSpec';

export type FilterPanelProps = HTMLAttributes<HTMLDivElement>;

const FilterPanel = ({ className, ...props }: FilterPanelProps) => {
  const [isMobileExpanded, , toggleMobileMenuState] = useMobileExpanded(false);
  const { filters, updateFilters, onClear, withSearch, sortByOptions, loading, activeTab } = useFiltersView();
  const hideOverdue = useFeature(FeatureToggleCodename.hideOverdueDates);
  const filteredSortByOptions = sortByOptions.filter(option => !hideOverdue || option !== Api.SortBy.dueDate);

  const closeBtnAriaLabel = useAttributeLabel(ATTRIBUTES.ARIA_LABEL, COMMON_LABELS.DIALOG.CLOSE);

  const expandBtnAriaLabel = useAttributeLabel(
    ATTRIBUTES.ARIA_LABEL,
    FILTER_LABELS.EXPAND_FILTERS_MOBILE_ICON_ARIA_LABEL,
  );
  const device = useDevice();
  const { facets, search, sortBy, sortDirection } = filters;

  const handleFacetChange = <T extends FacetType>(facet: Facet<T>) =>
    updateFilters({ ...filters, facets: updateByProp(facets, facet, 'name') });

  const handleSearchChange = useDebouncedCallback(
    (value: string) => updateFilters({ ...filters, search: value }),
    DEFAULT_DEBOUNCE_TIME,
  );

  const handleSortByChange = (updatedSortBy: FilterSortBy, updatedSortDirection: Api.SortDirection) => {
    updateFilters({
      ...filters,
      sortBy: updatedSortBy,
      sortDirection: updatedSortDirection,
    });
  };

  const handleClear: ReactEventHandler<HTMLButtonElement> = event => {
    event.preventDefault();

    onClear();
  };

  const facetViews = facets.map(facet => <FacetView {...facet} key={facet.name} onChange={handleFacetChange} />);

  useEffect(() => {
    document.body.style.overflow = isMobileExpanded ? 'hidden' : '';
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileExpanded]);

  return (
    <aside
      className={classes(className, styles.filterWrapper, {
        [styles.wrapperOverlayed]: device !== 'desktop' && isMobileExpanded,
      })}
      aria-labelledby={FILTER_LABELS.FILTERS_LABEL}
      {...props}
    >
      <div className={classes(styles.filtersTopContainer, { [styles.filtersTopLearningContainer]: activeTab })}>
        <div className={styles.filtersTitle}>
          <FiltersIcon className={styles.filtersIcon} />
          <h3 className={styles.filtersTitleText} id={FILTER_LABELS.FILTERS_LABEL}>
            <Label name={FILTER_LABELS.FILTERS_LABEL} />
          </h3>
          {(device === 'desktop' || isMobileExpanded) && (
            <button className={styles.clearBtn} onClick={handleClear}>
              <Label name={FILTER_LABELS.CLEAR_LABEL} />
            </button>
          )}
          <WithDevice mobile tablet>
            {isMobileExpanded ? (
              <button className={styles.mobileCloseBtn} {...closeBtnAriaLabel} onClick={toggleMobileMenuState}>
                <CloseIcon />
              </button>
            ) : (
              <button className={styles.mobileExpandButton} {...expandBtnAriaLabel} onClick={toggleMobileMenuState}>
                <RightArrow className={styles.mobileArrow} />
              </button>
            )}
          </WithDevice>
        </div>
        <WithDevice desktop>
          <FilterSearchBox value={search ?? ''} onChange={handleSearchChange} withSearch={withSearch} />
        </WithDevice>
      </div>
      <div
        className={classes(styles.mobileWrapper, {
          [styles.isMobileExpanded]: isMobileExpanded,
        })}
      >
        <WithDevice mobile tablet>
          <FilterSearchBox
            value={search ?? ''}
            onChange={handleSearchChange}
            className={styles.mobileSearchBox}
            withSearch={withSearch}
          />
        </WithDevice>
        {!!filteredSortByOptions.length && (
          <SortingFacet
            sortBy={sortBy ?? FILTERS_DEFAULT_SORTING_VALUES.SORT_BY}
            sortDirection={sortDirection ?? FILTERS_DEFAULT_SORTING_VALUES.SORT_DIRECTION}
            sortByOptions={filteredSortByOptions}
            onChange={handleSortByChange}
          />
        )}
        {loading === 'initial' ? <SkeletonFacets /> : facetViews}
      </div>
      <WithDevice mobile tablet>
        <div className={styles.mobileResultsButtonContainer}>
          <Button onClick={toggleMobileMenuState} className={styles.mobileResultsBtn}>
            <Label name={FILTER_LABELS.SHOW_RESULTS_MOBILE_LABEL} />
            {!isNil(filters.totalCount) && `(${filters.totalCount})`}
          </Button>
        </div>
      </WithDevice>
    </aside>
  );
};

export default FilterPanel;
